import { useEffect, useState } from "react";
import { Ship, Plane, Calculator, Info, Pencil } from "lucide-react";
import { api } from "../lib/api";
import { rupiah } from "../lib/format";

export const ShippingCalculator = ({ items }) => {
  const [config, setConfig] = useState(null);
  const [productMap, setProductMap] = useState({});
  const [mode, setMode] = useState("laut");
  const [city, setCity] = useState("");
  const [rates, setRates] = useState({});

  useEffect(() => {
    Promise.all([api.get("/shipping/config"), api.get("/products")]).then(([c, p]) => {
      setConfig(c.data);
      const map = {}; p.data.products.forEach((pr) => { map[pr.slug] = pr; });
      setProductMap(map);
      setRates(JSON.parse(JSON.stringify(c.data.category_rates)));
      setCity(c.data.cities[0].key);
    }).catch(() => {});
  }, []);

  if (!config || items.length === 0) return null;

  // group cart items by category
  const groups = {};
  items.forEach((it) => {
    const pr = productMap[it.slug];
    if (!pr) return;
    const g = groups[pr.category] || { category: pr.category, kg: 0, cbm: 0, units: 0 };
    g.kg += pr.weight_kg * it.qty;
    g.cbm += (pr.cbm_per_unit || 0) * it.qty;
    g.units += it.qty;
    groups[pr.category] = g;
  });
  const groupList = Object.values(groups);
  const cityObj = config.cities.find((c) => c.key === city) || config.cities[0];
  const mult = cityObj.multiplier;
  const totalKg = groupList.reduce((s, g) => s + g.kg, 0);

  const setRate = (cat, field, val) =>
    setRates((r) => ({ ...r, [cat]: { ...r[cat], [field]: Number(val) || 0 } }));

  let grand = 0;
  const rows = groupList.map((g) => {
    const r = rates[g.category] || config.category_rates[g.category] || { sea_per_cbm: 0, air_per_kg: 0 };
    let cost = 0, basisQty = 0, unitLabel = "";
    if (mode === "laut") {
      basisQty = g.cbm; unitLabel = "m³";
      cost = g.cbm * r.sea_per_cbm * mult;
    } else {
      const volKg = g.cbm * config.air_volumetric_factor;
      basisQty = Math.max(g.kg, volKg); unitLabel = "kg";
      cost = basisQty * r.air_per_kg * mult;
    }
    grand += cost;
    return { ...g, r, cost, basisQty, unitLabel };
  });

  const rateField = mode === "laut" ? "sea_per_cbm" : "air_per_kg";
  const rateUnit = mode === "laut" ? "/ m³" : "/ kg";
  const eta = mode === "laut" ? cityObj.eta_laut : cityObj.eta_udara;

  return (
    <div className="mt-8 bg-white rounded-2xl border border-stone-200 p-5 sm:p-6" data-testid="shipping-calculator">
      <div className="flex items-center gap-2 mb-1">
        <Calculator className="text-emerald-600" size={22} />
        <h2 className="font-display font-bold text-lg text-stone-900">Kalkulator Ongkir Transparan</h2>
      </div>
      <p className="text-sm text-stone-500 mb-5">Hitung sendiri estimasi ongkir. Tarif per kategori bisa kamu ubah agar transparan.</p>

      <div className="flex flex-wrap gap-4 items-end mb-5">
        {/* Mode */}
        <div>
          <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Metode</label>
          <div className="flex gap-2 mt-1.5">
            <button data-testid="ship-mode-laut" onClick={() => setMode("laut")} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition ${mode === "laut" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-stone-600 border-stone-200"}`}><Ship size={16} />Laut (CBM)</button>
            <button data-testid="ship-mode-udara" onClick={() => setMode("udara")} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition ${mode === "udara" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-stone-600 border-stone-200"}`}><Plane size={16} />Udara (Kg)</button>
          </div>
        </div>
        {/* City */}
        <div>
          <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Kota Tujuan</label>
          <select data-testid="ship-city-select" value={city} onChange={(e) => setCity(e.target.value)} className="mt-1.5 block border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-emerald-400">
            {config.cities.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
          </select>
        </div>
        <div className="text-sm text-stone-500">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Estimasi Tiba</p>
          <p className="mt-2 font-semibold text-stone-700">{eta}</p>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-xs text-stone-400 border-b border-stone-100">
              <th className="py-2 pr-2 font-semibold">Kategori</th>
              <th className="py-2 px-2 font-semibold">{mode === "laut" ? "Volume (m³)" : "Berat Tertagih (kg)"}</th>
              <th className="py-2 px-2 font-semibold">Tarif {rateUnit} <span className="text-emerald-600">(bisa diedit)</span></th>
              <th className="py-2 pl-2 font-semibold text-right">Ongkir</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g.category} data-testid={`ship-row-${g.category}`} className="border-b border-stone-50">
                <td className="py-3 pr-2">
                  <p className="font-medium text-stone-800">{g.category}</p>
                  <p className="text-xs text-stone-400">{g.units} pcs · {g.kg.toFixed(2)} kg · {g.cbm.toFixed(4)} m³</p>
                </td>
                <td className="py-3 px-2 font-mono">{g.basisQty.toFixed(mode === "laut" ? 4 : 2)} {g.unitLabel}</td>
                <td className="py-3 px-2">
                  <div className="flex items-center gap-1 bg-stone-50 border border-stone-200 rounded-lg px-2 py-1 w-36 focus-within:border-emerald-400">
                    <Pencil size={12} className="text-stone-400" />
                    <span className="text-xs text-stone-400">Rp</span>
                    <input data-testid={`ship-rate-${g.category}`} type="number" value={g.r[rateField]}
                      onChange={(e) => setRate(g.category, rateField, e.target.value)}
                      className="w-full bg-transparent outline-none text-sm font-mono" />
                  </div>
                </td>
                <td className="py-3 pl-2 text-right font-semibold text-stone-800">{rupiah(g.cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 bg-emerald-50 rounded-xl px-4 py-3">
        <div className="text-sm text-emerald-800">
          <span className="text-xs">Estimasi ongkir {mode === "laut" ? "laut" : "udara"} ke {cityObj.name}</span>
          <p className="font-display text-2xl font-extrabold text-emerald-700" data-testid="ship-total">{rupiah(grand)}</p>
        </div>
        {cityObj.multiplier !== 1 && <span className="text-xs text-emerald-700">Termasuk penyesuaian daerah ×{cityObj.multiplier}</span>}
      </div>

      <div className="mt-3 space-y-1.5 text-xs text-stone-500">
        <p className="flex items-start gap-1.5"><Info size={13} className="mt-0.5 shrink-0" /> <b className="text-stone-600">Laut</b> dihitung per CBM (m³). Minimal order laut {config.sea_min_kg} kg.</p>
        <p className="flex items-start gap-1.5"><Info size={13} className="mt-0.5 shrink-0" /> <b className="text-stone-600">Udara</b> pakai berat tertagih = mana yang lebih besar antara berat asli (kg) & berat volume (1 m³ = {config.air_volumetric_factor} kg).</p>
        <p className="flex items-start gap-1.5"><Info size={13} className="mt-0.5 shrink-0" /> Tarif berbeda tiap kategori karena pajak & penanganan. Angka ini estimasi — silakan ubah tarif sesuai kesepakatan.</p>
      </div>
    </div>
  );
};
